import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateAudit, type AuditRow } from '../../lib/api-client';

interface ValidationLockInOptions {
  audit: AuditRow;
  jwt?: string | null;
  refreshAudit: () => Promise<void>;
}

interface Props extends ValidationLockInOptions {
  locking?: boolean;
  lockError?: string | null;
  onLockIn?: () => Promise<void>;
}

export function useValidationLockIn({ audit, jwt, refreshAudit }: ValidationLockInOptions) {
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onLockIn = useCallback(async () => {
    if (!jwt || locking) return;
    setLocking(true);
    setLockError(null);
    try {
      await updateAudit(jwt, audit.id, { status: 'ready_to_materialize' });
      await refreshAudit();
      // Task 5 (#1738): locking in no longer jumps straight to the
      // status-PATCH → materialize flow. The owner reviews the exact
      // proposal (departments, seats, invites) and confirms from there.
      navigate(`/audits/${audit.id}/review`);
    } catch (err) {
      setLockError(err instanceof Error ? err.message : 'Could not lock the blueprint');
    } finally {
      setLocking(false);
    }
  }, [audit.id, jwt, locking, refreshAudit, navigate]);

  return { locking, lockError, onLockIn };
}

/**
 * The shared validation transition for both the default conversation view and
 * the selected-asset editor. Keeping the request state here prevents the two
 * surfaces from drifting into different lock-in behavior.
 */
export function ValidationLockInAction({
  audit,
  jwt,
  refreshAudit,
  locking: controlledLocking,
  lockError: controlledLockError,
  onLockIn: controlledOnLockIn,
}: Props) {
  const localLockIn = useValidationLockIn({ audit, jwt, refreshAudit });
  const locking = controlledLocking ?? localLockIn.locking;
  const lockError = controlledLockError ?? localLockIn.lockError;
  const onLockIn = controlledOnLockIn ?? localLockIn.onLockIn;

  return (
    <div className="flex items-center gap-2" data-testid="validation-toggle">
      <button
        type="button"
        disabled={!jwt || locking}
        onClick={() => void onLockIn()}
        className="rounded bg-coral px-2 py-1 text-xs text-[#0B0E14] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {locking ? 'Locking…' : 'Looks good — lock it in'}
      </button>
      {lockError && <span role="alert" className="text-xs text-red-600">{lockError}</span>}
    </div>
  );
}
